import { DefaultContext, useMutation, useQuery } from '@apollo/client'
import { CircularProgress, InputAdornment } from '@material-ui/core'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'

import {
  AccountType,
  DeletePayoutMethodDocument,
  PayoutMethodAchDocument,
  UpdatePayoutMethodAchDocument,
  UpdatePayoutMethodAchInput,
} from '../../../../../../graphql/search'
import useLogAndCaptureError from '../../../../../../hooks/useLogAndCaptureError'
import { accountTypeOptions } from '../../../../../../lib/account-documents/types'
import { ApolloClientsContext } from '../../../../../../lib/graphql/apollo'
import { logAndCaptureException } from '../../../../../../utils/errorTools'
import { checkQueryResponse } from '../../../../../../utils/graphqlTools'
import { MutationErrorAlert } from '../../../../../MutationErrorAlert'
import Spacer from '../../../../../Spacer'
import { ConfirmationModal } from '../ConfirmationModal'
import * as Styled from './styled'
import { validateAccountNumber, validateRoutingNumber } from './validation'

import checkmarkCircleIcon from '../../../../../../img/checked-circle.svg'

export interface AchAccountFormProps {
  className?: string
  handleHideModal: () => void
}

interface FormValues {
  accountHolderName: string
  bankName: string
  routingNumber: string
  accountNumber: string
  accountNumberConfirm: string
  accountType: AccountType
}

export const AchAccountForm: React.FC<AchAccountFormProps> = ({ className, handleHideModal }) => {
  const { comma: commaClient } = useContext(ApolloClientsContext)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const methods = useForm<FormValues>()

  const { formState, handleSubmit, getValues, reset, watch, setError, errors } = methods
  const accountNumber = watch('accountNumber')
  const accountNumberConfirm = watch('accountNumberConfirm')
  const accountNumbersMatch =
    accountNumber === accountNumberConfirm && accountNumber !== '' && accountNumber !== undefined

  const { data, error: payoutMethodError } = useQuery(PayoutMethodAchDocument, {
    client: commaClient,
    context: { useLatestTokenIfLoggedIn: true } as DefaultContext,
  })

  useLogAndCaptureError(payoutMethodError)

  const achDetails = data?.customer?.payoutMethod

  const defaultValues: FormValues = useMemo(
    () => ({
      accountHolderName: achDetails?.accountHolderName ?? '',
      accountNumber: achDetails?.accountNumber ?? '',
      accountNumberConfirm: achDetails?.accountNumber ?? '',
      accountType: achDetails?.accountType ?? 'CHECKING',
      bankName: achDetails?.bankName ?? '',
      routingNumber: achDetails?.routingNumber ?? '',
    }),
    [achDetails],
  )

  useEffect(() => {
    reset(defaultValues)
  }, [defaultValues, reset])

  const [updatePayoutMethodAch, { loading: updatePayoutMethodLoading }] = useMutation(
    UpdatePayoutMethodAchDocument,
    {
      client: commaClient,
      context: { useLatestTokenIfLoggedIn: true } as DefaultContext,
    },
  )
  const saveDisabled = updatePayoutMethodLoading

  const onSubmit: SubmitHandler<FormValues> = async (formValues: FormValues) => {
    const input: UpdatePayoutMethodAchInput = {
      accountType: formValues.accountType,
      accountHolderName: formValues.accountHolderName,
      accountNumber: formValues.accountNumber,
      bankName: formValues.bankName,
      routingNumber: formValues.routingNumber,
    }
    const result = await updatePayoutMethodAch({
      variables: { input },
    })
      .then(checkQueryResponse)
      .then(() => {
        commaClient.cache.evict({ id: 'ROOT_QUERY', fieldName: 'customer' })
      })
      .then(() => {
        setIsModalOpen(false)
      })
      .then(() => {
        handleHideModal()
      })
      .then(() => ({ error: undefined }))
      .catch((err) => {
        const error = err instanceof Error ? err : new Error(`${err}`)
        logAndCaptureException(error)
        return { error }
      })
    if (result.error) {
      setError('accountHolderName', { message: result.error.message, type: 'server' })
    }
  }

  const [deletePayoutMethod, { loading: deletePayoutMethodLoading }] = useMutation(
    DeletePayoutMethodDocument,
    {
      client: commaClient,
      context: { useLatestTokenIfLoggedIn: true } as DefaultContext,
    },
  )

  const handleDelete = async () => {
    const result = await deletePayoutMethod()
      .then(checkQueryResponse)
      .then(() => {
        commaClient.cache.evict({ id: 'ROOT_QUERY', fieldName: 'customer' })
      })
      .then(() => {
        setIsModalOpen(false)
        handleHideModal()
      })
      .then(() => ({ error: undefined }))
      .catch((err) => {
        const error = err instanceof Error ? err : new Error(`${err}`)
        logAndCaptureException(error)
        return { error }
      })
    if (result.error) {
      setError('accountHolderName', { message: result.error.message, type: 'server' })
    }
  }

  const hasServerError = !!errors.accountHolderName && errors.accountHolderName.type === 'server'

  return (
    <FormProvider {...methods}>
      <Styled.Form className={className} onSubmit={handleSubmit(onSubmit)}>
        {hasServerError && !isModalOpen && (
          <MutationErrorAlert
            onCancel={() => reset(defaultValues)}
            onRetry={handleSubmit(onSubmit)}
          >
            An error occurred modifying your account details, and we have been notified.
          </MutationErrorAlert>
        )}
        <Styled.AccountInformationHeader>
          <Styled.FormTitle>Account information</Styled.FormTitle>
          <Styled.DeleteAccountDetails onClick={() => setIsModalOpen(true)}>
            Delete account details
          </Styled.DeleteAccountDetails>
        </Styled.AccountInformationHeader>
        <Styled.FirstRow>
          <Styled.RequiredTextField
            label="Name of the bank account holder"
            name="accountHolderName"
            defaultValue={achDetails?.accountHolderName}
          />
          <Styled.RequiredTextField
            label="Bank name"
            name="bankName"
            defaultValue={achDetails?.bankName}
          />
        </Styled.FirstRow>
        <Spacer size={4} />
        <Styled.FormTitle>Account details</Styled.FormTitle>
        <Styled.SecondRow>
          <Styled.RequiredTextField
            label="Routing number"
            name="routingNumber"
            inputMode="numeric"
            defaultValue={achDetails?.routingNumber}
            optionalRules={{
              validate: validateRoutingNumber,
            }}
            autoComplete="off"
          />
          <Styled.RequiredTextField
            label="Account number"
            name="accountNumber"
            inputMode="numeric"
            defaultValue={achDetails?.accountNumber}
            optionalRules={{
              validate: validateAccountNumber,
            }}
            autoComplete="off"
          />
          <Styled.RequiredTextField
            label="Repeat account number"
            name="accountNumberConfirm"
            inputMode="numeric"
            defaultValue={achDetails?.accountNumber}
            InputProps={{
              endAdornment: accountNumbersMatch ? (
                <InputAdornment position="end">
                  <Styled.CheckmarkCircleIcon src={checkmarkCircleIcon} />
                </InputAdornment>
              ) : null,
            }}
            optionalRules={{
              validate: (value: string) =>
                value === getValues('accountNumber') || 'Account numbers must match',
            }}
            autoComplete="off"
          />
          <Styled.RequiredSelectField
            label="Account type"
            name="accountType"
            defaultValue={achDetails?.accountType}
            options={accountTypeOptions}
          />
        </Styled.SecondRow>
        <Styled.Buttons>
          <Styled.SaveButton type="submit" disabled={formState.isSubmitting || saveDisabled}>
            {formState.isSubmitting ? <CircularProgress size={24} color="inherit" /> : 'Save'}
          </Styled.SaveButton>
          <Styled.CancelButton disabled={formState.isSubmitting} onClick={handleHideModal}>
            Cancel
          </Styled.CancelButton>
        </Styled.Buttons>
      </Styled.Form>
      <ConfirmationModal
        isOpen={isModalOpen}
        title="Are you sure you want to delete your account details?"
        description="Account information will be deleted from our records and payments will not be processed through ACH."
        onCancel={() => setIsModalOpen(false)}
        onConfirm={handleDelete}
        confirmButtonText={
          <>
            {deletePayoutMethodLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Yes, delete'
            )}
          </>
        }
        cancelButtonText={<>Go back</>}
        disabled={deletePayoutMethodLoading}
      >
        {hasServerError && isModalOpen && (
          <MutationErrorAlert
            onCancel={() => reset(defaultValues)}
            onRetry={handleSubmit(onSubmit)}
          >
            An error occurred modifying your account details, and we have been notified.
          </MutationErrorAlert>
        )}
      </ConfirmationModal>
    </FormProvider>
  )
}
